import {
	refreshFriendRequestIndicator,
	startFriendRequestIndicator,
	stopFriendRequestIndicator,
} from '../components/FriendRequestIndicator'
import { apiUrl } from '../config/environment'
import { parseServerSentEventBlock } from '../utils/serverSentEvents'
import { authenticatedFetch } from './authenticatedFetch'
import { invalidateFriendsRequest } from './social'

let eventStreamController: AbortController | null = null

/** Starts the shared authenticated app event stream once. */
export function initAppEvents(): void {
	if (document.getElementById('inner-content')) {
		startFriendRequestIndicator()
	}
	if (eventStreamController) return

	const controller = new AbortController()
	eventStreamController = controller
	void streamAppEvents(controller)
}

/** Closes the shared app event stream and stops friend-request polling. */
export function closeAppEvents(): void {
	eventStreamController?.abort()
	eventStreamController = null
	stopFriendRequestIndicator()
}

async function streamAppEvents(controller: AbortController): Promise<void> {
	let retryDelay = 1000
	let reconnecting = false
	try {
		while (!controller.signal.aborted) {
			const startedAt = Date.now()
			try {
				if (!(await readAppEvents(controller, reconnecting))) return
			} catch (error) {
				if (controller.signal.aborted) return
				console.error('App event stream failed:', error)
			}
			if (controller.signal.aborted) return
			if (Date.now() - startedAt >= 30_000) retryDelay = 1000
			await waitToReconnect(controller.signal, retryDelay)
			retryDelay = Math.min(retryDelay * 2, 30_000)
			reconnecting = true
		}
	} finally {
		if (eventStreamController === controller) eventStreamController = null
	}
}

function waitToReconnect(signal: AbortSignal, delay: number): Promise<void> {
	return new Promise((resolve) => {
		const finish = () => {
			clearTimeout(timer)
			signal.removeEventListener('abort', finish)
			window.removeEventListener('online', finish)
			window.removeEventListener('focus', finish)
			document.removeEventListener('visibilitychange', onVisible)
			resolve()
		}
		const onVisible = () => {
			if (document.visibilityState === 'visible') finish()
		}
		const timer = setTimeout(finish, delay)
		signal.addEventListener('abort', finish, { once: true })
		window.addEventListener('online', finish, { once: true })
		window.addEventListener('focus', finish, { once: true })
		document.addEventListener('visibilitychange', onVisible)
		if (signal.aborted) finish()
	})
}

async function readAppEvents(
	controller: AbortController,
	reconnecting: boolean,
): Promise<boolean> {
	const response = await authenticatedFetch(apiUrl('/api/events'), {
		headers: { Accept: 'text/event-stream' },
		signal: controller.signal,
	})
	if (response.status === 401) {
		const { handleAuthFailure } = await import('./auth')
		await handleAuthFailure('session-expired')
		return false
	}
	if (!response.ok || !response.body) {
		throw new Error(`App event stream failed with ${response.status}.`)
	}
	if (controller.signal.aborted) return false
	if (reconnecting) {
		// Events missed during a disconnect are not replayed by the server.
		invalidateFriendsRequest()
		void refreshFriendRequestIndicator()
	}
	const reader = response.body.getReader()
	try {
		const decoder = new TextDecoder()
		let buffer = ''
		while (!controller.signal.aborted) {
			const { done, value } = await reader.read()
			if (done) break
			buffer += decoder.decode(value, { stream: true })
			buffer = consumeEventBlocks(buffer)
		}
	} finally {
		reader.releaseLock()
	}
	return true
}

function consumeEventBlocks(value: string): string {
	let buffer = value
	while (true) {
		const boundary = /\r?\n\r?\n/.exec(buffer)
		if (!boundary || typeof boundary.index === 'undefined') return buffer

		const event = parseServerSentEventBlock(buffer.slice(0, boundary.index))
		if (event?.event === 'friendships-changed') {
			invalidateFriendsRequest()
			void refreshFriendRequestIndicator()
		}
		buffer = buffer.slice(boundary.index + boundary[0].length)
	}
}
